import { Component, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

@Component({
  selector: "app-json-viewer-dialog",
  templateUrl: "./json-viewer-dialog.component.html",
  styleUrls: ["./json-viewer-dialog.component.scss"]
})
export class JsonViewerDialogComponent {
  parsedData: any = null;
  parseError: string = "";
  title: string = "JSON Viewer";
  coordinates: { lat: number; lng: number } | null = null;
  mapUrl: string = "";

  constructor(
    private dialogRef: MatDialogRef<JsonViewerDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: JsonViewerDialogData
  ) {
    this.title = data.title || "JSON Viewer";
    this.parseJsonString(data.jsonString);
  }

  parseJsonString(jsonString: string): void {
    if (!jsonString) {
      this.parseError = "No data provided";
      return;
    }

    try {
      // Try to parse the JSON string
      this.parsedData = JSON.parse(jsonString);
      this.parseError = "";
      this.extractCoordinates(this.parsedData);
    } catch (error) {
      this.parseError = "Invalid JSON format";
      // Try to display as plain text if parsing fails
      this.parsedData = null;
      console.error("JSON parse error:", error);
    }
  }

  extractCoordinates(data: any): void {
    if (!data || typeof data !== "object") return;

    // Look for common coordinate property names
    const latKeys = ["lat", "latitude", "Lat", "Latitude"];
    const lngKeys = ["lng", "long", "longitude", "lang", "Lng", "Long", "Longitude", "Lang"];

    let lat: number | null = null;
    let lng: number | null = null;

    // Search for latitude
    for (const key of latKeys) {
      if (data[key] !== undefined && data[key] !== null) {
        const value = parseFloat(data[key]);
        if (!isNaN(value)) {
          lat = value;
          break;
        }
      }
    }

    // Search for longitude
    for (const key of lngKeys) {
      if (data[key] !== undefined && data[key] !== null) {
        const value = parseFloat(data[key]);
        if (!isNaN(value)) {
          lng = value;
          break;
        }
      }
    }

    // If both coordinates found, store them
    if (lat !== null && lng !== null) {
      this.coordinates = { lat, lng };
      this.mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    }
  }

  getMapUrl(): string {
    if (!this.coordinates) return "";
    return `https://www.google.com/maps?q=${this.coordinates.lat},${this.coordinates.lng}`;
  }

  openInMaps(): void {
    if (this.coordinates) {
      window.open(this.getMapUrl(), "_blank");
    }
  }

  isObject(value: any): boolean {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  isPrimitive(value: any): boolean {
    return value === null || (typeof value !== "object");
  }

  getKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  formatValue(value: any): string {
    if (value === null) return "null";
    if (value === undefined) return "undefined";
    if (typeof value === "string") return value;
    return String(value);
  }

  formatKey(key: string): string {
    // Convert camelCase or PascalCase to readable format
    // Example: firstName -> First name, emailAddress -> Email address
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  getValueClass(value: any): { [key: string]: boolean } {
    return {
      "json-string": typeof value === "string",
      "json-number": typeof value === "number",
      "json-boolean": typeof value === "boolean",
      "json-null": value === null
    };
  }

  onClose(): void {
    this.dialogRef.close();
  }
}

export interface JsonViewerDialogData {
  jsonString: string;
  title?: string;
}
